import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchPublicSettings } from "../lib/api";
import { Button } from "../components/ui/button";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { ArrowRight, Check } from "lucide-react";

export default function Pricing() {
  const { type = "manual" } = useParams();
  const transmission = type === "automatic" ? "auto" : "manual";
  const [settings, setSettings] = useState(null);
  useEffect(() => { fetchPublicSettings().then(setSettings).catch(() => {}); }, []);

  const baseRate = transmission === "manual" ? settings?.manual_hourly : settings?.auto_hourly;
  // Discount lookup using flexible tiers (fallback to legacy fields)
  const bestDiscount = (slots) => {
    if (!settings) return 0;
    const tiers = settings.discount_tiers || [];
    let best = 0;
    tiers.forEach(t => { if (slots >= (t.min_lessons || 0) && (t.discount_per_hour || 0) > best) best = t.discount_per_hour; });
    if (best > 0) return best;
    if (slots >= 10) return settings.discount_20h || 0;
    if (slots >= 5) return settings.discount_10h || 0;
    return 0;
  };

  const productToPackageSlug = {
    standard: "standard",
    package10: "10-hour",
    package20: "20-hour",
    bespoke: "bespoke",
  };

  const products = [
    { key: "standard", title: "Standard 2-Hour Lesson", hours: 2, slots: 1, rate: baseRate, perks: ["Pay-as-you-go", "Door-to-door pickup", "All cars dual-controlled"] },
    { key: "package10", title: "10-Hour Package", hours: 10, slots: 5, rate: baseRate ? baseRate - bestDiscount(5) : null, perks: ["5 × 2-hour lessons", `Save £${bestDiscount(5).toFixed(2)}/hr`, "Book scattered slots"] },
    { key: "package20", title: "20-Hour Package", hours: 20, slots: 10, rate: baseRate ? baseRate - bestDiscount(10) : null, perks: ["10 × 2-hour lessons", `Save £${bestDiscount(10).toFixed(2)}/hr`, "Best value"] },
    { key: "bespoke", title: "Intensive Courses", hours: null, slots: 0, rate: null, perks: ["Pass fast — 10, 20 or 35 hours", "Southampton & Winchester test centres", "Manual and automatic"] },
  ];

  return (
    <>
      <Seo
        title={transmission === "manual"
          ? "Manual Driving Lessons Pricing — Lime Driving School"
          : "Automatic Driving Lessons Pricing — Lime Driving School"}
        description={`Transparent pricing for ${transmission === "manual" ? "manual" : "automatic"} driving lessons in Southampton.`}
      />
      <PageHero
        eyebrow="Pricing"
        title={transmission === "manual" ? "Manual lesson pricing" : "Automatic lesson pricing"}
        subtitle={transmission === "manual"
          ? "Manual lessons available Monday to Thursday and Saturday — three fixed 2-hour slots per day."
          : "Automatic lessons exclusively on Fridays — three fixed 2-hour slots per day."}
      />
      <section className="py-12 md:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid={`pricing-${transmission}`}>
        <div className="flex items-baseline gap-4 mb-6">
          <Link to="/pricing/manual" className={`text-sm ${transmission==='manual' ? 'text-[#111827] font-medium border-b-2 border-[#37B160] pb-1' : 'text-[#9CA3AF]'}`} data-testid="pricing-tab-manual">Manual</Link>
          <Link to="/pricing/automatic" className={`text-sm ${transmission==='auto' ? 'text-[#111827] font-medium border-b-2 border-[#37B160] pb-1' : 'text-[#9CA3AF]'}`} data-testid="pricing-tab-automatic">Automatic</Link>
          <span className="text-sm text-[#4B5563] ml-auto">Base rate: <strong>£{baseRate?.toFixed(2) || "—"}/hr</strong></span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {products.map(p => {
            const isBespoke = p.key === "bespoke";
            const total = p.rate && p.hours ? p.rate * p.hours : null;
            const featured = p.key === "package10";
            return (
              <div key={p.key}
                   className={`bg-white border rounded-2xl p-6 flex flex-col ${featured ? 'border-[#37B160] ring-2 ring-[#37B160]/30' : 'border-black/5'}`}
                   data-testid={`pricing-card-${p.key}`}>
                {featured && <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#37B160] mb-3">Most popular</div>}
                <h3 className="text-lg font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{p.title}</h3>
                <div className="mt-4">
                  {isBespoke ? (
                    <>
                      <div className="text-3xl font-medium" style={{fontFamily:"Outfit, sans-serif"}}>From £500</div>
                      <div className="text-xs text-[#9CA3AF] mt-1">10, 20 or 35 hour options</div>
                    </>
                  ) : (
                    <>
                      <div className="text-3xl font-medium" style={{fontFamily:"Outfit, sans-serif"}}>£{total?.toFixed(0)}</div>
                      <div className="text-xs text-[#9CA3AF] mt-1">£{p.rate?.toFixed(2)}/hr</div>
                    </>
                  )}
                </div>
                <ul className="mt-5 space-y-2 text-sm text-[#4B5563]">
                  {p.perks.map((pk, j) => <li key={j} className="flex items-start gap-2"><Check className="h-4 w-4 text-[#37B160] mt-0.5 flex-none" /> {pk}</li>)}
                </ul>
                <div className="mt-6 pt-2 mt-auto">
                  {isBespoke ? (
                    <Link to="/packages/bespoke" data-testid={`pricing-${p.key}-cta`}>
                      <Button variant="outline" className="w-full border-black/15">Explore courses <ArrowRight className="h-4 w-4 ml-1" /></Button>
                    </Link>
                  ) : (
                    <Link to={`/packages/${productToPackageSlug[p.key]}`} data-testid={`pricing-${p.key}-cta`}>
                      <Button className="w-full bg-[#37B160] hover:bg-[#2D9450] text-white">
                        Book now <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
